<script>
import { mapGetters } from 'vuex';
import MentionBox from '../mentions/MentionBox.vue';

export default {
  components: { MentionBox },
  props: {
    searchKey: {
      type: String,
      default: '',
    },
  },
  emits: ['replace', 'attach', 'button'],
  computed: {
    ...mapGetters({
      cannedMessages: 'getCannedResponses',
    }),
    // Two different jobs, so two different orderings.
    //
    // SEARCHING — the API has already ranked the results (CannedResponse.order_by_search:
    // exact shortcode > shortcode prefix > shortcode contains > content contains, with
    // deterministic tiebreakers) and hands us the best match FIRST. Do not re-group:
    // emit that order verbatim and let a folder label repeat if the ranking interleaves
    // folders. Any regrouping demotes the top hit — that is precisely the bug here.
    // PR #139 re-sorted alphabetically by category, so "/شكر" highlighted whatever sorted
    // first; grouping by best-ranked folder is no better, because it drags the whole of
    // that folder above an equally-ranked match sitting in another one ("/تم" landed on
    // «تم تسجيل», an order-confirmation, instead of the teacher-registration form).
    //
    // BROWSING (no search term) — there is no ranking to preserve, so group into folders
    // exactly the way the settings page does: named folders alphabetically, uncategorized
    // last, shortcodes alphabetical inside each.
    items() {
      const uncategorized = this.$t('CANNED_MGMT.LIST.UNCATEGORIZED');
      const folderOf = response => response.category || uncategorized;
      const toItem = (cannedMessage, folderHeader) => ({
        id: cannedMessage.id,
        label: cannedMessage.short_code,
        key: cannedMessage.short_code,
        description: cannedMessage.content,
        attachments: cannedMessage.attachments || [],
        buttonLabel: cannedMessage.button_label || null,
        folderHeader,
      });

      const responses = [...this.cannedMessages];

      if (this.searchKey) {
        let lastFolder = null;
        return responses.map(response => {
          const folder = folderOf(response);
          const header = folder === lastFolder ? null : folder;
          lastFolder = folder;
          return toItem(response, header);
        });
      }

      const folders = new Map();
      responses.forEach(response => {
        const folder = folderOf(response);
        if (!folders.has(folder)) folders.set(folder, []);
        folders.get(folder).push(response);
      });

      const orderedFolders = [...folders.keys()].sort((a, b) => {
        if (a === uncategorized) return 1;
        if (b === uncategorized) return -1;
        return a.localeCompare(b);
      });

      return orderedFolders.flatMap(folder =>
        [...folders.get(folder)]
          .sort((a, b) =>
            (a.short_code || '').localeCompare(b.short_code || '')
          )
          .map((cannedMessage, indexInFolder) =>
            toItem(cannedMessage, indexInFolder === 0 ? folder : null)
          )
      );
    },
  },
  watch: {
    searchKey() {
      this.fetchCannedResponses();
    },
  },
  mounted() {
    this.fetchCannedResponses();
  },
  methods: {
    fetchCannedResponses() {
      this.$store.dispatch('getCannedResponse', { searchKey: this.searchKey });
    },
    handleMentionClick(item = {}) {
      if (item.id) {
        this.$store.dispatch('markCannedResponseUsed', item.id);
      }
      this.$emit('replace', item.description);
      if (item.attachments && item.attachments.length) {
        this.$emit('attach', item.attachments);
      }
      // Always emitted: inserting a button-less canned response clears any
      // pending button a previous insert left behind.
      this.$emit('button', item.buttonLabel);
    },
  },
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <MentionBox
    v-if="items.length"
    :items="items"
    @mention-select="handleMentionClick"
  />
</template>
